/* import __COLOCATED_TEMPLATE__ from './transfer-credit-modal.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { COMPANY_PROFILE_COMPONENT } from 'sh-www/components/company-profile/company-profile';
import { BUTTON_TYPES } from 'sh-www/constants/lsg';
import { showNextModal, closeModal } from 'sh-www/utils/modal-controls';
import { makeMoney } from 'sh-www/helpers/make-money';
import {
  clearErrors,
  handleErrorResponse,
} from 'sh-www/utils/handles-errors';

const CLASS_NAME = 'SH-c-transfer-credit-modal';

const INLINE_ERRORS = [
  'generic-exception',
  'amount_in_cents',
  'integrity_checksum',
  'target_cost_code_id',
];

class TransferCreditModal extends Component {
  @service constants;
  @service('commands/cost-code-commands') costCodeCommandService;
  @service session;

  @tracked errors = {};
  @tracked paymentAmount = null;
  @tracked sourceCostCode = null;
  @tracked destinationCostCode = null;

  get className() {
    return CLASS_NAME;
  }

  get inlineErrors() {
    return INLINE_ERRORS;
  }

  get sourceCostCodeValue() {
    return this.sourceCostCode?.body;
  }

  get sourceCostCodeOptions() {
    const costCodes = this.args.model.costCodes ?? [];

    if (!this.destinationCostCode) {
      return costCodes;
    }

    return costCodes.filter((costCode) => (
      costCode.id !== this.destinationCostCode.id
    ));
  }

  get destinationCostCodeOptions() {
    const costCodes = this.args.model.costCodes ?? [];

    if (!this.sourceCostCode) {
      return costCodes;
    }

    return costCodes.filter((costCode) => (
      costCode.id !== this.sourceCostCode.id
    ));
  }

  get destinationCostCodeValue() {
    return this.destinationCostCode?.body;
  }

  get costCodesSectionEnum() {
    return COMPANY_PROFILE_COMPONENT.SECTIONS.CODES;
  }

  get submitDisabled() {
    return !this.sourceCostCode
      || !this.destinationCostCode
      || (
        !this.paymentAmount
        || Number.parseFloat(this.paymentAmount) <= 0
      )
      || this.transferCredit.isRunning;
  }

  get maxCreditAvailableInDollars() {
    if (!this.sourceCostCode) {
      return null;
    }

    return Number.parseFloat((this.sourceCostCode.availableCreditInCents / 100).toFixed(2));
  }

  @dropTask
  *transferCredit() {
    clearErrors(this);

    const {
      sourceCostCode,
      sourceCostCodeValue,
      destinationCostCode,
      destinationCostCodeValue,
      paymentAmount,
    } = this;

    const {
      loadCompanyCostCodeCreditsTask,
      updateCompanyCostCodeCredits,
    } = this.args.model;

    const amountInCents = paymentAmount * 100;
    const amountUSD = makeMoney([paymentAmount]);

    try {
      // perform transfer
      yield this.costCodeCommandService.transferCreditToCostCode(
        sourceCostCode.id,
        destinationCostCode.id,
        amountInCents,
        sourceCostCode.integrityChecksum,
      );

      // reload balances
      const companyCostCodeCredits = yield loadCompanyCostCodeCreditsTask.perform();
      updateCompanyCostCodeCredits(
        companyCostCodeCredits.costCodes,
        companyCostCodeCredits.costCodesCurrentPage,
      );

      // show success
      showNextModal(this, 'confirmation', {
        classNames: `${CLASS_NAME}__success-modal`,
        titleText: 'Transfer successful',
        bodyText: `<strong>${amountUSD}</strong> transferred from <strong>${sourceCostCodeValue}</strong> to <strong>${destinationCostCodeValue}</strong>.`,
        actionButtonText: 'Got it',
        width: 'medium',
        actionButtonLsgButtonType: BUTTON_TYPES.PRIMARY_REGULAR,
        iconName: 'swap_horiz',
        isDismissible: false,
      });
    } catch (e) {
      handleErrorResponse(this, e, INLINE_ERRORS, true, true);
    }
  }

  @action
  handleSourceCostCodeSelected(costCode) {
    this.sourceCostCode = costCode;

    const { maxCreditAvailableInDollars, paymentAmount } = this;
    if (costCode && maxCreditAvailableInDollars && paymentAmount > maxCreditAvailableInDollars) {
      this.paymentAmount = maxCreditAvailableInDollars;
    }
  }

  @action
  handleDestinationCostCodeSelected(costCode) {
    this.destinationCostCode = costCode;
  }

  @action
  handlePaymentAmountInput(amount) {
    const { maxCreditAvailableInDollars } = this;

    if (maxCreditAvailableInDollars !== null && amount > maxCreditAvailableInDollars) {
      this.paymentAmount = maxCreditAvailableInDollars;
    } else {
      this.paymentAmount = amount;
    }
  }

  @action
  handlePaymentAmountBlur() {
    const { paymentAmount } = this;
    const paymentAmountFloat = Number.parseFloat(paymentAmount);

    // clear bad input
    if (!Number.isFinite(paymentAmountFloat) || paymentAmountFloat <= 0) {
      this.paymentAmount = '';
    } else {
      this.paymentAmount = makeMoney([paymentAmount], { commas: false }).replace('$', '');
    }
  }

  @action
  handleDismiss() {
    closeModal(this);
  }
}

export default TransferCreditModal;
